/**
 * Device safe-area helpers (notch / status bar / home indicator).
 *
 * The app sets `viewport-fit=cover`, so on notched mobile devices content can
 * render under the notch and home indicator. These helpers wrap the CSS
 * `env(safe-area-inset-*)` values so overlays/panels/modals inset their content
 * consistently. Every value falls back to `0px`, so on desktop and non-notched
 * devices the helpers are a no-op.
 */
import { CSSProperties } from 'react';

export const SAFE_AREA_TOP = 'env(safe-area-inset-top, 0px)';
export const SAFE_AREA_BOTTOM = 'env(safe-area-inset-bottom, 0px)';
export const SAFE_AREA_LEFT = 'env(safe-area-inset-left, 0px)';
export const SAFE_AREA_RIGHT = 'env(safe-area-inset-right, 0px)';

/**
 * Pad a full-bleed panel inward by the device safe-area insets on all sides.
 * The panel background still paints behind the insets (e.g. under the notch);
 * only its content is inset. Use for full-screen slide-in panels.
 */
export const safeAreaPanelInsets: CSSProperties = {
  paddingTop: SAFE_AREA_TOP,
  paddingBottom: SAFE_AREA_BOTTOM,
  paddingLeft: SAFE_AREA_LEFT,
  paddingRight: SAFE_AREA_RIGHT
};

/**
 * Backdrop padding for a centered modal: a base gutter plus the safe-area inset
 * on each side, so the centered card never reaches under the notch / home
 * indicator. `base` accepts any CSS length (default `1rem`).
 */
export const safeAreaModalBackdropPadding = (base = '1rem'): CSSProperties => ({
  paddingTop: `calc(${base} + ${SAFE_AREA_TOP})`,
  paddingRight: `calc(${base} + ${SAFE_AREA_RIGHT})`,
  paddingBottom: `calc(${base} + ${SAFE_AREA_BOTTOM})`,
  paddingLeft: `calc(${base} + ${SAFE_AREA_LEFT})`
});

/**
 * Subtract the vertical safe-area insets from a viewport-height budget so a
 * height-capped element (e.g. a modal `maxHeight`) leaves room for the notch
 * and home indicator. Pass the inner budget expression, e.g. `'100vh - 3rem'`.
 */
export const safeAreaMaxHeight = (budget: string): string =>
  `calc(${budget} - ${SAFE_AREA_TOP} - ${SAFE_AREA_BOTTOM})`;
